/**
 * Telegram-style mosaic layout algorithm for media groups.
 *
 * Computes optimal rectangle packing for 1–10 media items within a
 * fixed max width (512px by default). Tries multiple layout candidates
 * (row-based and column-based splits) and picks the one with the best
 * overall proportions.
 *
 * Coordinates are returned as fractions [0..1] of the total bounding box,
 * making the layout fully responsive via percentage-based CSS.
 */

export interface MosaicItem {
  width: number;
  height: number;
}

export interface MosaicRect {
  index: number;
  /** Fraction of total width [0..1] */
  x: number;
  /** Fraction of total height [0..1] */
  y: number;
  /** Fraction of total width [0..1] */
  w: number;
  /** Fraction of total height [0..1] */
  h: number;
}

export interface MosaicLayout {
  /** Total bounding box width in px (= maxWidth) */
  width: number;
  /** Total bounding box height in px */
  height: number;
  /** Positioned rectangles in fractional coordinates */
  rects: MosaicRect[];
}

const GAP = 2;
const MAX_HEIGHT = 512;
const MIN_HEIGHT = 100;
const MIN_ITEM_DIM = 30;

/** Clamp aspect ratio to sane bounds. Default 1:1 for unknown dimensions. */
function clampRatio(w: number, h: number): number {
  if (w <= 0 || h <= 0) return 1;
  return Math.max(0.25, Math.min(10, w / h));
}

// ── Row-based layout ────────────────────────────────────────────────

interface RawRect {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface RawLayout {
  width: number;
  height: number;
  rects: RawRect[];
}

/**
 * Arrange items into horizontal rows.
 * `split` defines how many items per row, e.g. [2,3] = 2 on top, 3 on bottom.
 */
function computeRowLayout(
  ratios: number[],
  split: number[],
  maxWidth: number,
): RawLayout {
  const rects: RawRect[] = [];
  let idx = 0;
  let y = 0;

  for (const count of split) {
    const rowRatios = ratios.slice(idx, idx + count);
    const sumR = rowRatios.reduce((a, b) => a + b, 0);
    const rowH = (maxWidth - (count - 1) * GAP) / sumR;

    let x = 0;
    for (let i = 0; i < count; i++) {
      const w = rowRatios[i]! * rowH;
      rects.push({ index: idx + i, x, y, width: w, height: rowH });
      x += w + GAP;
    }
    y += rowH + GAP;
    idx += count;
  }

  return { width: maxWidth, height: y - GAP, rects };
}

// ── Column-based layout ─────────────────────────────────────────────

/**
 * Split items into a left column and a right column, both sharing the
 * same total height. Solves for column widths algebraically.
 */
function computeColumnLayout(
  ratios: number[],
  leftCount: number,
  maxWidth: number,
): RawLayout | null {
  const rightCount = ratios.length - leftCount;
  if (leftCount < 1 || rightCount < 1) return null;

  const leftR = ratios.slice(0, leftCount);
  const rightR = ratios.slice(leftCount);

  // Sum of inverse ratios per column (height-per-unit-width)
  const sL = leftR.reduce((s, r) => s + 1 / r, 0);
  const sR = rightR.reduce((s, r) => s + 1 / r, 0);
  const gL = (leftCount - 1) * GAP;
  const gR = (rightCount - 1) * GAP;

  const denom = sL + sR;
  if (denom === 0) return null;

  const wL = ((maxWidth - GAP) * sR + gR - gL) / denom;
  const wR = maxWidth - GAP - wL;

  // Reject degenerate solutions
  if (wL < MIN_ITEM_DIM || wR < MIN_ITEM_DIM) return null;

  const totalH = wL * sL + gL;
  if (totalH <= 0) return null;

  const rects: RawRect[] = [];

  // Left column
  let y = 0;
  for (let i = 0; i < leftCount; i++) {
    const h = wL / leftR[i]!;
    rects.push({ index: i, x: 0, y, width: wL, height: h });
    y += h + GAP;
  }

  // Right column
  y = 0;
  for (let i = 0; i < rightCount; i++) {
    const h = wR / rightR[i]!;
    rects.push({ index: leftCount + i, x: wL + GAP, y, width: wR, height: h });
    y += h + GAP;
  }

  return { width: maxWidth, height: totalH, rects };
}

// ── Candidate generation ────────────────────────────────────────────

type Candidate =
  | { type: 'row'; split: number[] }
  | { type: 'col'; leftCount: number };

function getCandidates(n: number): Candidate[] {
  const cs: Candidate[] = [];

  if (n === 1) {
    // Handled separately, but include for completeness
    cs.push({ type: 'row', split: [1] });
    return cs;
  }

  // Single row (up to 4 items)
  if (n <= 4) {
    cs.push({ type: 'row', split: [n] });
  }

  // 2-row splits
  for (let top = 1; top < n; top++) {
    cs.push({ type: 'row', split: [top, n - top] });
  }

  // 3-row splits (5+ items)
  if (n >= 5) {
    for (let r1 = 1; r1 <= Math.min(4, n - 2); r1++) {
      for (let r2 = 1; r2 <= Math.min(4, n - r1 - 1); r2++) {
        const r3 = n - r1 - r2;
        if (r3 >= 1 && r3 <= 4) {
          cs.push({ type: 'row', split: [r1, r2, r3] });
        }
      }
    }
  }

  // Column splits (left + right, max 4 per side)
  if (n >= 2 && n <= 8) {
    for (let left = 1; left < n; left++) {
      const right = n - left;
      if (left <= 4 && right <= 4) {
        cs.push({ type: 'col', leftCount: left });
      }
    }
  }

  return cs;
}

// ── Scoring ─────────────────────────────────────────────────────────

function scoreLayout(layout: RawLayout): number {
  const totalRatio = layout.width / layout.height;

  // Prefer overall aspect ratio close to 1.2 (slightly wider than tall)
  const ratioPenalty = Math.abs(Math.log(totalRatio / 1.2));

  // Penalize extreme heights
  let heightPenalty = 0;
  if (layout.height > MAX_HEIGHT) {
    heightPenalty = (layout.height - MAX_HEIGHT) / MAX_HEIGHT;
  } else if (layout.height < MIN_HEIGHT) {
    heightPenalty = (MIN_HEIGHT - layout.height) / MIN_HEIGHT;
  }

  // Penalize tiny items
  let sizePenalty = 0;
  for (const r of layout.rects) {
    if (r.width < MIN_ITEM_DIM || r.height < MIN_ITEM_DIM) {
      sizePenalty += 1;
    }
  }

  return ratioPenalty + heightPenalty * 2 + sizePenalty * 5;
}

// ── Public API ──────────────────────────────────────────────────────

/**
 * Compute optimal mosaic layout for a list of media items.
 *
 * Returns fractional coordinates so the layout works at any container
 * width via CSS percentages. Also returns the absolute pixel height
 * for setting `aspect-ratio` on the container.
 */
export function computeMosaicLayout(
  items: MosaicItem[],
  maxWidth = 512,
): MosaicLayout {
  const n = items.length;
  if (n === 0) {
    return { width: maxWidth, height: 0, rects: [] };
  }

  const ratios = items.map((it) => clampRatio(it.width, it.height));

  // ── Single item: special case ──
  if (n === 1) {
    const r = ratios[0]!;
    const w = maxWidth;
    let h = w / r;
    h = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, h));

    return {
      width: w,
      height: h,
      rects: [{ index: 0, x: 0, y: 0, w: 1, h: 1 }],
    };
  }

  // ── Multi-item: try all candidates, pick best ──
  const candidates = getCandidates(n);
  let best: RawLayout | null = null;
  let bestScore = Infinity;

  for (const c of candidates) {
    let layout: RawLayout | null;
    if (c.type === 'row') {
      layout = computeRowLayout(ratios, c.split, maxWidth);
    } else {
      layout = computeColumnLayout(ratios, c.leftCount, maxWidth);
    }

    if (!layout) continue;

    // Skip invalid layouts
    const invalid = layout.rects.some((r) => r.width <= 0 || r.height <= 0);
    if (invalid || layout.height <= 0) continue;

    const score = scoreLayout(layout);
    if (score < bestScore) {
      bestScore = score;
      best = layout;
    }
  }

  // Fallback: single row
  if (!best) {
    best = computeRowLayout(ratios, [n], maxWidth);
  }

  // Clamp total height
  let totalH = best.height;
  if (totalH > MAX_HEIGHT) {
    const scale = MAX_HEIGHT / totalH;
    for (const r of best.rects) {
      r.y *= scale;
      r.height *= scale;
    }
    totalH = MAX_HEIGHT;
  }

  // Convert to fractional coordinates
  const rects: MosaicRect[] = best.rects.map((r) => ({
    index: r.index,
    x: r.x / maxWidth,
    y: r.y / totalH,
    w: r.width / maxWidth,
    h: r.height / totalH,
  }));

  return { width: maxWidth, height: totalH, rects };
}
